import { useContext, useEffect, useState, useCallback } from "react";
import { CiSearch } from "react-icons/ci";
import { BsFillXCircleFill } from "react-icons/bs";
import toast from "react-hot-toast";
import moment from "moment";
import "../../styles/adminUsers.css";
import "../../styles/TransactionHistory.css";
import "../../styles/ReportManage.css";
import Select from "react-select";
import TransactionHistoryServices from "../../services/TransactionHistoryServices";
import TransactionDetailsModal from "./modals/TransactionDetailsModal";
import { AuthContext } from "../../context/AuthContext";
import BalanceLog from "./components/BalanceLog";
import BalanceLogDownload from "./components/BalanceLogDownload";
import BalanceHistory from "./components/BalanceHistory";
import BalanceHistoryDownload from "./components/BalanceHistoryDownload";
import BalanceProfitLoss from "./components/BalanceProfitLoss";
import TransactionHistoryDownload from "./components/TransactionHistoryDownload";
import TransactionHistoryComponent from "./components/TransactionHistory";
import { selectCustomStyles } from "../../utils/utils";
import MobileTransactionHistory from "./components/MobileTransactionHistory";

const Tabs = [
  { label: "Balance Log", value: "balance_log" },
  { label: "Balance History", value: "balance_history" },
  { label: "Balance Profit/Loss", value: "balance_profit_loss" },
  { label: "Transaction History", value: "transaction_history" },
];

const TransactionHistory = () => {
  const { user } = useContext(AuthContext);
  const txApi = new TransactionHistoryServices();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [activeTab, setActiveTab] = useState((user.role_id === 3 || user.role_id === 4) ? "transaction_history" : "balance_log");
  const [userId, setUserId] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [outletUsers, setOutletUsers] = useState([]);
  const [mobileUsers, setMobileUsers] = useState([]);

  const [q, setQ] = useState("");
  const [from, setFrom] = useState(
    moment().startOf("month").format("YYYY-MM-DD"),
  );
  const [to, setTo] = useState(moment().format("YYYY-MM-DD"));
  const [paymentType, setPaymentType] = useState("");
  const [gameType, setGameType] = useState("");

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const loadBalanceLog = async () => {
    try {
      const res = await txApi.getBalanceLogHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };
  const loadBalanceHistory = async () => {
    try {
      const res = await txApi.getBalanceHistory({
        page,
        limit,
        q,
        from,
        to,
        userId,
        paymentType,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceProfileLoss = async () => {
    try {
      const res = await txApi.getBalanceProfileLoss({
        page,
        limit,
        q,
        from,
        to,
        userId,
      });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadTransactionHistory = async () => {
    try {
      const res =
        user.role_id === 4
          ? await txApi.getMobileTransactionHistory({
              page,
              limit,
              q,
              from,
              to,
              userId,
              gameType,
            })
          : await txApi.getTransactionHistory({
              page,
              limit,
              q,
              from,
              to,
              userId,
              gameType,
            });
      if (res.isSuccess) {
        setRows(res.data || []);
        setPagination(res.pagination);
      } else {
        toast.error(res.message || "Failed to load transactions");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to load transactions",
      );
    } finally {
      setLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    const res = await txApi.getAdminUsers();
    if (res.isSuccess) {
      setAdminUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load admin users");
    }
  };
  const loadOutletUsers = async () => {
    const res = await txApi.getOutletUsers();
    if (res.isSuccess) {
      setOutletUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load outlet users");
    }
  };

  const loadMobileUsers = async () => {
    const res = await txApi.getMobileUsers();
    if (res.isSuccess) {
      setMobileUsers(res.data || []);
    } else {
      toast.error(res.message || "Failed to load mobile users");
    }
  };

  useEffect(() => {
    if (user.role_id === 1) {
      loadAdminUsers();
    }
    if (user.role_id === 2 || user.role_id === 1) {
      loadOutletUsers();
    }
    if (user.role_id === 2 || user.role_id === 1 || user.role_id === 3) {
      loadMobileUsers();
    }
  }, []);

  const loadTx = useCallback(async () => {
    setLoading(true);
    if (activeTab === "balance_log") {
      await loadBalanceLog();
    } else if (activeTab === "balance_history") {
      await loadBalanceHistory();
    } else if (activeTab === "balance_profit_loss") {
      await loadBalanceProfileLoss();
    } else if (activeTab === "transaction_history") {
      await loadTransactionHistory();
    }
  }, [activeTab, page, limit, q, from, to, userId, gameType, paymentType]);

  useEffect(() => {
    loadTx();
  }, [loadTx]);

  const openDetails = async (id) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetails(null);
    try {
      const res = await txApi.getTransactionDetails(id);
      if (res.isSuccess) setDetails(res.data);
      else toast.error(res.message || "Failed to load details");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load details");
    } finally {
      setDetailsLoading(false);
    }
  };

  const onSearch = (e) => {
    setPage(1);
    setQ(e.target.value);
  };

  const onFrom = (e) => {
    setPage(1);
    setFrom(e.target.value);
  };

  const onTo = (e) => {
    setPage(1);
    setTo(e.target.value);
  };

  return (
    <div>
      <div className="tx-header">
        <div className="tx-header-filters-section">
          <div className="transaction-tab-wrapper">
            {Tabs.map((tab) => {
              if (tab.value === "balance_profit_loss" && user.role_id !== 1) {
                return null;
              }
              return (
                <button
                  key={tab.value}
                  className={activeTab === tab.value ? "active" : ""}
                  onClick={() => {
                    setUserId("");
                    setQ("");
                    setGameType("");
                    setPaymentType("");
                    setActiveTab(tab.value);
                    setPage(1);
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="d-flex align-items-center gap-2">
            {activeTab === "balance_log" && (
              <BalanceLogDownload
                from={from}
                to={to}
                userId={userId}
                q={q}
                paymentType={paymentType}
                userName={
                  [...adminUsers, ...outletUsers, ...mobileUsers].find(
                    (u) => Number(u.id) === Number(userId),
                  )?.name ||
                  (user?.name ?? "All")
                }
              />
            )}
            {activeTab === "balance_history" && (
              <BalanceHistoryDownload
                from={from}
                to={to}
                userId={userId}
                q={q}
                paymentType={paymentType}
                userName={
                  [...adminUsers, ...outletUsers, ...mobileUsers].find(
                    (u) => Number(u.id) === Number(userId),
                  )?.name ||
                  (user?.name ?? "All")
                }
              />
            )}
            {activeTab === "transaction_history" && (
              <>
                <TransactionHistoryDownload
                  from={from}
                  to={to}
                  userId={userId}
                  q={q}
                  gameType={gameType}
                  userName={
                    [...adminUsers, ...outletUsers, ...mobileUsers].find(
                      (u) => Number(u.id) === Number(userId),
                    )?.name ||
                    (user?.name ?? "All")
                  }
                />
                <select
                  className="form-control"
                  value={gameType}
                  onChange={(e) => {
                    setGameType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Select Session</option>
                  <option value="3min">3min</option>
                  <option value="5min">5min</option>
                </select>
              </>
            )}
            {(activeTab === "balance_log" ||
              activeTab === "balance_history") && (
              <select
                className="form-control"
                value={paymentType}
                onChange={(e) => {
                  setPaymentType(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Select Type</option>
                <option value="CREDIT">Credit</option>
                <option value="DEBIT">Debit</option>
              </select>
            )}
            <button
              type="button"
              className="transaction-clear-button"
              onClick={() => {
                setUserId("");
                setQ("");
                setGameType("");
                setPaymentType("");
                setFrom(moment().startOf("month").format("YYYY-MM-DD"));
                setTo(moment().format("YYYY-MM-DD"));
                setPage(1);
              }}
            >
              {/* <BsFillXCircleFill size={18} className="search-icon" /> */}
              <span>Clear</span>
            </button>
          </div>
        </div>

        <div className="tx-header-row">
          <div className="admin-search tx-search">
            <div className="search-box" style={{ width: "100%" }}>
              <CiSearch size={18} className="search-icon" />
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search (reference, user, type...)"
                value={q}
                onChange={onSearch}
              />
            </div>
          </div>
          {user.role_id === 1 &&
            activeTab !== "transaction_history" &&
            activeTab !== "balance_profit_loss" && (
              <div>
                <Select
                  styles={selectCustomStyles}
                  aria-labelledby="aria-label"
                  inputId="admin-users-select"
                  instanceId="admin-users-select"
                  name="admin-users-select"
                  isSearchable={true}
                  openMenuOnFocus={true}
                  filterOption={(option, inputValue) =>
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  options={adminUsers.map((u) => ({
                    value: Number(u.id),
                    label: u.username,
                  }))}
                  value={
                    adminUsers
                      .map((u) => ({ value: Number(u.id), label: u.username }))
                      .find((o) => o.value === Number(userId)) || null
                  }
                  onChange={(opt) => {
                    setUserId(opt?.value ?? null);
                    setPage(1);
                  }}
                  placeholder="Select Admin"
                  isClearable={true}
                />
              </div>
            )}
          {(user.role_id === 1 || user.role_id === 2) && (
            <div>
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="outlet-users-select"
                instanceId="outlet-users-select"
                name="outlet-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={outletUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  outletUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                }}
                placeholder="Select Outlet"
                isClearable={true}
              />
            </div>
          )}
          {(user.role_id === 1 || user.role_id === 2 || user.role_id === 3) && (
            <div>
              <Select
                styles={selectCustomStyles}
                aria-labelledby="aria-label"
                inputId="mobile-users-select"
                instanceId="mobile-users-select"
                name="mobile-users-select"
                isSearchable={true}
                openMenuOnFocus={true}
                options={mobileUsers.map((u) => ({
                  value: Number(u.id),
                  label: u.username,
                }))}
                value={
                  mobileUsers
                    .map((u) => ({ value: Number(u.id), label: u.username }))
                    .find((o) => o.value === Number(userId)) || null
                }
                onChange={(opt) => {
                  setUserId(opt?.value ?? null);
                  setPage(1);
                }}
                placeholder="Select Mobile"
                isClearable={true}
              />
            </div>
          )}

          <div className="tx-filters">
            <div className="tx-date">
              <label>From</label>
              <input type="date" value={from} onChange={onFrom} />
            </div>
            <div className="tx-date">
              <label>To</label>
              <input type="date" value={to} onChange={onTo} />
            </div>
          </div>
        </div>
      </div>

      <div>
        <>
          {activeTab === "balance_log" && (
            <BalanceLog
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "balance_history" && (
            <BalanceHistory
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "balance_profit_loss" && (
            <BalanceProfitLoss
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          )}
          {activeTab === "transaction_history" && user.role_id !== 4 ? (
            <TransactionHistoryComponent
              userId={userId}
              rows={rows}
              loading={loading}
              page={page}
              pagination={pagination}
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              user={user}
              setDetailsOpen={setDetailsOpen}
              setDetailsLoading={setDetailsLoading}
              setDetails={setDetails}
              openDetails={openDetails}
            />
          ) : (
            activeTab === "transaction_history" && (
              <MobileTransactionHistory
                userId={userId}
                rows={rows}
                loading={loading}
                page={page}
                pagination={pagination}
                limit={limit}
                setLimit={setLimit}
                setPage={setPage}
                user={user}
                setDetailsOpen={setDetailsOpen}
                setDetailsLoading={setDetailsLoading}
                setDetails={setDetails}
                openDetails={openDetails}
              />
            )
          )}
        </>
      </div>

      <TransactionDetailsModal
        show={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        loading={detailsLoading}
        data={details}
      />
    </div>
  );
};

export default TransactionHistory;
